import React, { useCallback } from 'react'
import * as R from 'ramda'

import FilterGroup from 'shared/components/FilterGroup'
import { FilterGroupProvider } from 'shared/components/FilterGroup/context'
import useCustomAttributeOptions from 'hooks/useCustomAttributeOptions'

import type { OnChangeValue } from 'shared/components/FilterGroup'
import type { WidgetSettingsState } from 'components/Admin/FeedbackWidgetSettings'
import type { Option, Extra } from 'organisms/FeedbackFilterGroup/container'
import CustomFilterValueInputForIsComparison from 'components/CustomFilterValueInputForIsComparison'

export default function ScoreWidgetFilter({
  widgetSettingsState,
  setWidgetState,
}: ScoreWidgetFilterProps) {
  const handleChange = useCallback(
    function handleAnswerFilterChange(filter: OnChangeValue<any>) {
      setWidgetState((prevData) => ({
        ...prevData,
        answerFilter: convertFilterToRequest(filter),
      }))
    },
    [setWidgetState]
  )

  const customAttributeOptions = useCustomAttributeOptions()
  const isCustomAttributeOptionsLoading = customAttributeOptions == null
  const isLoading = !widgetSettingsState?.answerFilter || isCustomAttributeOptionsLoading

  if (isLoading) {
    return null
  }

  const options = baseFilterOptions.concat(customAttributeOptions)
  const defaultValue = convertAnswerFilterResponseToRules(
    widgetSettingsState?.answerFilter,
    options
  )

  return (
    <FilterGroupProvider>
      <FilterGroup
        condition="and"
        onChange={handleChange}
        options={options}
        defaultValue={defaultValue}
        comparisons={[
          { label: 'is', value: 'is', inputComponent: CustomFilterValueInputForIsComparison },
        ]}
      />
    </FilterGroupProvider>
  )
}

function convertAnswerFilterResponseToRules(
  answerFilter: { and: AnswerFilterRule[] },
  options: FilterOption[]
): FilterOption[] {
  if (R.prop('and', answerFilter)) {
    return answerFilter.and.map((rule) => {
      const { filtered_values } = rule
      const filters = filtered_values.map((filtered_value) => {
        return { value: filtered_value, label: filtered_value }
      })

      const selectedOption = findSelectedOption(rule, options)
      return {
        ...(selectedOption as object),
        filters,
      } as FilterOption
    })
  } else {
    return []
  }
}

function findSelectedOption(rule: AnswerFilterRule, options: FilterOption[]): FilterOption {
  const value = `${rule.key}${rule?.attribute || ''}`
  return R.find((option) => {
    const optionValue = `${option.field}${option?.extra?.attribute || ''}`
    return value === optionValue
  }, options) as FilterOption
}

const baseFilterOptions: Option<Extra>[] = [
  {
    field: 'team_member',
    label: 'Team member',
    icon: 'far fa-user-headset',
    comparison: 'is',
  },
  {
    field: 'customer',
    label: 'Customer',
    icon: 'far fa-user',
    comparison: 'is',
  },
  {
    field: 'company',
    label: 'Company',
    icon: 'building',
    comparison: 'is',
  },
  {
    field: 'tag',
    label: 'Tag',
    icon: 'tag',
    comparison: 'is',
  },
  {
    field: 'customer.tag',
    label: 'Customer Tag',
    icon: 'tag',
    comparison: 'is',
  },
  {
    field: 'language',
    label: 'Language',
    icon: 'far fa-globe',
    comparison: 'is',
  },
]

function convertFilterToRequest(filter: OnChangeValue<any>) {
  if (filter) {
    return {
      [filter.condition]: filter.rules.map((rule) => {
        return {
          key: rule.field,
          filtered_values: rule.filters.map((filter) => filter.value),
          attribute: rule.extra?.attribute,
          comparison: rule.comparison,
        }
      }),
    }
  }
}

type FilterOption = {
  field: string
  extra: { attribute: string }
  label: string
  icon: string
  inputComponent: React.Component
  comparison: string
  filters: { value: string; label: string }[]
}

type AnswerFilterRule = {
  key: string
  filtered_values: string[]
  attribute?: string
}
export type AnswerFilter = { and: AnswerFilterRule[] }

type ScoreWidgetFilterProps = {
  widgetSettingsState: WidgetSettingsState
  setWidgetState: Function
}



// WEBPACK FOOTER //
// ./src/organisms/ScoreWidgetFilter/index.tsx